import { useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { type ActionFunctionArgs, type LoaderFunctionArgs, redirect } from '@remix-run/node';
import { useActionData, useSearchParams } from '@remix-run/react';
import { useEffect } from 'react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { toast } from 'sonner';
import { Login, loginSchema } from '#common/features/auth/Login/index';
import { commitFlashSession, getFlashSession } from '#common/utils/flashSession.server';
import { checkHoneypot } from '#common/utils/honeypot.server';
import { translatePath } from '#common/utils/i18n.tsx';
import { safeRedirect } from '#common/utils/misc';
import { defaultLang } from '#speaker-admin/i18n/config.ts';
import { getLocale, getMetaTitle } from '#speaker-admin/i18n/i18n.server';
import { verifyLogin } from '#speaker-admin/models/user.server';
import { getUserId, handleNewSession } from '#speaker-admin/session.server';
import { tenants } from '../../../../../other/tenants';

export const handle = { i18n: [ 'login' ] };

export const loader = async ({ request }: LoaderFunctionArgs) => {
    const session = await getFlashSession(request.headers.get('cookie'));
    const successMessage = session.get('success');

    const userId = await getUserId(request);
    if (userId) {
        throw redirect('/');
    }

    const url = new URL(request.url);
    const locale = await getLocale(null, request);

    return typedjson({
        title: await getMetaTitle(request, 'login'),
        successMessage,
        createAccountUrl: new URL(
            translatePath('/pricing', locale, { defaultLang }),
            `${url.protocol}//${tenants.website.hosts[0]}`
        ).toString()
    }, {
        headers: { 'Set-Cookie': await commitFlashSession(session) }
    });
};

export const action = async ({ request }: ActionFunctionArgs) => {
    const formData = await request.formData();
    await checkHoneypot(formData);

    const submission = parseWithZod(formData, { schema: loginSchema });
    if (submission.status !== 'success') {
        return typedjson(
            { result: submission.reply() },
            { status: submission.status === 'error' ? 400 : 200 }
        );
    }

    const redirectTo = safeRedirect(submission.value.redirectTo, '/');

    const user = await verifyLogin(submission.value.email, submission.value.password);

    if (!user) {
        return typedjson(
            submission.reply({
                fieldErrors: {
                    email: [ 'Invalid email or password' ],
                    password: [ 'Invalid email or password' ]
                }
            }),
            { status: 400 }
        );
    }

    return handleNewSession({
        redirectTo,
        remember: submission.value.remember ?? false,
        request,
        userId: user.id,
        userEmail: user.email
    });
};

export default function LoginPage() {
    const [ searchParams ] = useSearchParams();
    const { successMessage, createAccountUrl } = useTypedLoaderData<typeof loader>();
    const redirectTo = searchParams.get('redirectTo') || '/';
    const actionData = useActionData<typeof action>();
    const [ form, fields ] = useForm({
        lastResult: actionData,
        constraint: getZodConstraint(loginSchema),
        shouldValidate: 'onBlur',
        shouldRevalidate: 'onInput',
        onValidate({ formData }) {
            return parseWithZod(formData, { schema: loginSchema });
        }
    });

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
        }
    }, [ successMessage ]);

    return <Login fields={fields} form={form} redirectTo={redirectTo} createAccountUrl={createAccountUrl} />;
}
